import React, { Fragment, useRef } from 'react';
import { Loader } from '@toluna-ui-toolkit/loader';
import {
    modalCustomStyles,
    ImportProjectAppWrapper,
    ImportProjectModalBody,
    ImportProjectWrapper,
    ImportProjectModalHeader,
    CloseButton,
    ImportProjectModalContainer,
    HeaderTitle,
    InputItem,
    Label,
    DropDown,
    dropdownCustomStyle,
    Button,
    ImportProjectModalFooter,
    Icon,
    InputContainer,
    Modal,
    loaderCustomStyles,
    ImportProjectModalFooterErrorContainer
} from './ImportProject.style';
import { Input } from './Input';
import { Table } from './Table';
import { useImportProjectHook } from './useImportProject.hook';

export interface IImportProjectProps {
    showModal?: boolean;
    onClose?: () => void;
    translations?: Record<string, string>;
}

export const ImportProject = (props: IImportProjectProps) => {
    const {
        showImportProjectModal,
        isLoadImportProjectModal,
        projectType,
        projectId,
        checkedItems,
        importProject,
        notImportableReasonsAsExceptions,
        isLoadImportingProject,
        isImportDisabled,
        projectIdError,
        projectTypeError,
        importProjectError,
        handleShowDropdownOptions,
        handleResetSearch,
        handleHideModal,
        handleCheckItems,
        handleUncheckItems,
        handleSearchProject,
        handleImportProjects,
        debouncedHandleProjectChange,
        handleSetProjectType,
    } = useImportProjectHook(props);

    const { translations } = props;
    const scrollBarRef = useRef<React.ElementRef<typeof Table>>(null);

    if (!showImportProjectModal) {
        return <Fragment />;
    }

    const InputFieldActionButton = () => {
        switch (true) {
            case isLoadImportProjectModal:
                return <Loader innerColor="#cfd9db" customStyle={loaderCustomStyles()} />;

            case Boolean(importProject?.length):
                return <Icon onClick={handleResetSearch} className="fas fa-times" />;

            default:
                return <Icon enabled onClick={handleSearchProject} className="fas fa-search" />;
        }
    };

    const ModalHeader = () => {
        return (
            <ImportProjectModalHeader>
                <HeaderTitle>{translations?.TI2020_ContentTranslations_Header_ImportProject}</HeaderTitle>
                <CloseButton
                    role="button"
                    type="button"
                    aria-label="Cancel"
                    onClick={handleHideModal}
                />
            </ImportProjectModalHeader>
        );
    };

    const ModalBody = () => {
        return (
            <ImportProjectModalBody>
                <InputItem>
                    <Label>{translations?.TI2020_ContentTranslations_Header_ProjectID}</Label>
                    <InputContainer>
                        <Input
                            error={Boolean(projectIdError)}
                            disabled={isLoadImportProjectModal || Boolean(importProject?.length)}
                            defaultValue={projectId}
                            placeholder={translations?.TI2020_ContentTranslations_Header_TypeUPSProjectIDHere}
                            onChange={debouncedHandleProjectChange}
                        />
                        <InputFieldActionButton />
                    </InputContainer>
                    <Label isError>{projectIdError}</Label>
                </InputItem>
                {importProject?.length! && (
                    <Table ref={scrollBarRef} translations={translations} data={importProject} options={{ checkedItems, onChecked: handleCheckItems, onUnChecked: handleUncheckItems }} />
                )}
                <InputItem withMargin>
                    <Label>{translations?.TI2020_ContentTranslations_Header_ProjectType_ProjectType}</Label>
                    <DropDown
                        hasError={Boolean(projectTypeError)}
                        disabled={isLoadImportProjectModal}
                        value={projectType}
                        customStyle={dropdownCustomStyle}
                        placeholder={translations?.TI2020_ContentTranslations_Header_PleaseSelect}
                        options={handleShowDropdownOptions()}
                        onChange={handleSetProjectType}
                    />
                    <Label isError>{projectTypeError}</Label>
                </InputItem>
            </ImportProjectModalBody>
        );
    };

    const ModalFooter = () => {
        return (
            <>
                <ImportProjectModalFooter>
                    <Button onClick={handleImportProjects} disabled={isImportDisabled} isLoading={isLoadImportingProject}>
                        {translations?.TI2020_ContentTranslations_Header_Import}
                        {isLoadImportingProject && <Loader innerColor="#cfd9db" customStyle={loaderCustomStyles(true)} />}
                    </Button>
                    <Button onClick={handleHideModal} buttonType="outlined">
                        {translations?.TI2020_ContentTranslations_Header_Cancel}
                    </Button>
                </ImportProjectModalFooter>
                {importProjectError && (
                    <ImportProjectModalFooterErrorContainer>
                        <Label isError>{importProjectError}</Label>
                    </ImportProjectModalFooterErrorContainer>
                )}
                {Array.isArray(notImportableReasonsAsExceptions) && notImportableReasonsAsExceptions?.map((message, index) => (
                    <ImportProjectModalFooterErrorContainer key={index}>
                        <Label isError>{message}</Label>
                    </ImportProjectModalFooterErrorContainer>
                ))}
            </>
        );
    };

    return (
        <ImportProjectWrapper>
            <Modal customStyles={modalCustomStyles} showModal={showImportProjectModal}>
                <ImportProjectModalContainer>
                    <ImportProjectAppWrapper>
                        <ModalHeader />
                        <ModalBody />
                        <ModalFooter />
                    </ImportProjectAppWrapper>
                </ImportProjectModalContainer>
            </Modal>
        </ImportProjectWrapper>
    );
};

export default ImportProject;
